// src/components/Apm/Tables/TableSimple.js

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { styled } from '@mui/material/styles';
import {
  Box,
  Grid,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MarkunreadIcon from '@mui/icons-material/Markunread';
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread';

import { setItemForEditBasic } from '../../../redux/slices/itemForEditBasicSlice';
import { setItemForEdit } from '../../../redux/slices/itemForEditSlice';
import { setNotification } from '../../../redux/slices/notificationSlice';
import { setPopup } from '../../../redux/slices/popupSlice';
import { setPopupBasic } from '../../../redux/slices/popupBasicSlice';
import { setFormSimpleTouched } from '../../../redux/slices/formTouchedSlice';
import { useRetrieveUserQuery } from '../../../redux/features/authApi';
import ButtonAction from '../../Controls/Buttons/ButtonAction';
import ConfirmDialog from '../../Controls/Popups/ConfirmDialog';
import LinearProgressWrapper from '../../Controls/FormsUI/LinearProgress';
import Popup from '../../Controls/Popups/Popup';
import PopupBasic from '../../Controls/Popups/PopupBasic';
import QuickSearchInput from '../../Controls/Inputs/QuickSearchInput';
import BankAccount from '../BankAccount/BankAccount';
import Contact from '../Contact/Contact';
import ContractTerm from '../Term/ContractTerm';
import ItemInv from '../ItemInv/ItemInv';
import TermOfPayment from '../Term/TermOfPayment';
import SignUpAddUser from '../../Pages/Authentication/SignUpAddUser';
import { AccountProfileDetails } from '../Profile/AccountProfileDetails';
import { columnsPersons } from './Columns/columnsPersons';
import { maxNumberAllowedPerSubscription, pages } from '../../Data/generalData';

//----------------------------------- UTILS -----------------------------------//

const TableStyled = styled(Table)(({ theme }) => ({
  marginTop: theme.spacing(1.5),

  '& thead th': {
    fontWeight: 500,
    color: theme.palette.primary.dark,
    backgroundColor: '#9adcfb',
  },

  '& tbody tr:hover': {
    backgroundColor: '#fffbf2',
    cursor: 'pointer',
  },
}));

const TableRowStyled = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },

  '&:hover': {
    backgroundColor: '#fffbf2',
    cursor: 'pointer',
  },
}));

const TableCellStyled = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {},
  [`&.${tableCellClasses.body}`]: {},
}));

const TypographyPopover = styled(Typography)(({ theme }) => ({
  padding: 16,
  color: 'rgba(0, 0, 0, 0.87)',
  maxWidth: 350,
  fontSize: theme.typography.pxToRem(20),
}));

const RESOURCE_WITHOUT_ACTIONS = new Set(['countries', 'currencies']);

const LIMIT_INDEXES = {
  users: {
    active_free: 0,
    active_pro: 1,
    active_premium: 2,
  },

  clients: {
    active_free: 6,
    active_pro: 7,
    active_premium: 8,
  },

  'items-for-item-inv': {
    active_free: 9,
    active_pro: 10,
    active_premium: 11,
  },
};

const getErrorStatus = (error) => {
  if (error && typeof error === 'object' && 'status' in error) {
    return error.status;
  }

  return null;
};

const normalizeSearchValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value).toLowerCase();
    } catch {
      return '';
    }
  }

  return String(value).toLowerCase();
};

const matchesDefaultSearch = (item, columns, target) => {
  return columns.some((column) => {
    return normalizeSearchValue(item?.[column.id]).includes(target);
  });
};

const matchesBankAccountSearch = (item, target) => {
  return [
    item?.currencycode?.currencycode || item?.currency_code?.currency_code,
    item?.bank_name,
    item?.bank_code,
    item?.iban_number,
    item?.bank_address,
  ].some((value) => normalizeSearchValue(value).includes(target));
};

const matchesUserSearch = (item, target) => {
  const role = item?.is_manager ? 'manager' : 'dispatcher';

  return [item?.first_name, item?.last_name, item?.email, item?.phone, role].some((value) =>
    normalizeSearchValue(value).includes(target)
  );
};

const matchesClientSearch = (item, target) => {
  const directValues = [
    item?.companyname,
    item?.fiscalcode,
    item?.vatcode,
    item?.phone,
    item?.email1,
    item?.countrycodelegal?.value,
    item?.zipcodelegal,
    item?.citylegal,
    item?.addresslegal,
    item?.countrycodepost?.value,
    item?.zipcodepost,
    item?.citypost,
    item?.addresspost,
    item?.comment1,
  ];

  const directMatch = directValues.some((value) => normalizeSearchValue(value).includes(target));

  if (directMatch) {
    return true;
  }

  return (item?.contact_persons ?? []).some((person) => {
    return columnsPersons().some((column) => normalizeSearchValue(person?.[column.id]).includes(target));
  });
};

const filterItems = ({ data, columns, resource, target }) => {
  if (!target) {
    return data;
  }

  return data.filter((item) => {
    switch (resource) {
      case 'bank-accounts':
        return matchesBankAccountSearch(item, target);

      case 'users':
        return matchesUserSearch(item, target);

      case 'clients':
        return matchesClientSearch(item, target);

      default:
        return matchesDefaultSearch(item, columns, target);
    }
  });
};

const descendingComparator = (first, second, orderBy) => {
  const firstValue = first?.[orderBy] ?? '';

  const secondValue = second?.[orderBy] ?? '';

  if (secondValue < firstValue) {
    return -1;
  }

  if (secondValue > firstValue) {
    return 1;
  }

  return 0;
};

const getComparator = (order, orderBy) => {
  if (!orderBy) {
    return () => 0;
  }

  return order === 'desc'
    ? (first, second) => descendingComparator(first, second, orderBy)
    : (first, second) => -descendingComparator(first, second, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilized = array.map((item, index) => [item, index]);

  stabilized.sort((first, second) => {
    const comparison = comparator(first[0], second[0]);

    if (comparison !== 0) {
      return comparison;
    }

    return first[1] - second[1];
  });

  return stabilized.map(([item]) => item);
};

const renderCellValue = ({ row, column, t }) => {
  if (column.id === 'contact_persons') {
    return (row?.contact_persons ?? [])
      .map((person) => `${person?.last_name ?? ''} ${person?.first_name ?? ''}`.trim())
      .join(' / ');
  }

  if (column.id === 'countrycodepost') {
    return [row?.countrycodepost?.value, row?.zipcodepost, row?.citypost].filter(Boolean).join(' ');
  }

  if (column.id.startsWith('currencycode.') || column.id === 'currency_code') {
    return row?.currencycode?.currencycode ?? row?.currencycode?.currency_code ?? '';
  }

  if (column.id === 'is_manager') {
    return row?.is_manager ? t('general.manager') : t('general.dispatcher');
  }

  const value = row?.[column.id];

  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  return '';
};

//----------------------------------- MAIN COMPONENT -----------------------------------//

const TableSimple = ({
  resource,
  popupTitle,
  addButtonText = '',
  columns = [],
  data = [],

  isFetching = false,
  isDeleting = false,

  onRefresh,
  onDelete,
  onMarkRead,
  onAfterDelete,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const theme = useTheme();

  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: account } = useRetrieveUserQuery();

  const { itemForEdit } = useSelector((state) => state.itemForEditSlice);

  const { notification } = useSelector((state) => state.notificationSlice);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
    type: '',
  });

  const [anchorEl, setAnchorEl] = useState(null);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(pages[1]);

  const [order, setOrder] = useState('asc');

  const [orderBy, setOrderBy] = useState('');

  const [search, setSearch] = useState('');

  const [rowSelected, setRowSelected] = useState(null);

  const openSubscriptionPopover = Boolean(anchorEl);

  const subscriptionType = account?.subscription_type ?? null;

  const isNotificationTable = addButtonText === '';

  const showActionsColumn = !RESOURCE_WITHOUT_ACTIONS.has(resource);

  const canShowAddButton = Boolean(addButtonText) && (resource !== 'users' || account?.is_manager === true);

  const limitIndex = LIMIT_INDEXES[resource]?.[subscriptionType];

  const addLimit = typeof limitIndex === 'number' ? maxNumberAllowedPerSubscription[limitIndex] : null;

  const disabledAddButton = addLimit !== null && data.length >= addLimit;

  const filteredItems = useMemo(() => {
    return filterItems({
      data,
      columns,
      resource,
      target: search,
    });
  }, [data, columns, resource, search]);

  const sortedItems = useMemo(() => {
    return stableSort(filteredItems, getComparator(order, orderBy));
  }, [filteredItems, order, orderBy]);

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;

    return sortedItems.slice(start, start + rowsPerPage);
  }, [sortedItems, page, rowsPerPage]);

  const emptyRows = Math.max(0, rowsPerPage - paginatedItems.length);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredItems.length / rowsPerPage) - 1);

    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [filteredItems.length, page, rowsPerPage]);

  useEffect(() => {
    setRowsPerPage(smMediaDown ? pages[0] : pages[1]);

    setPage(0);
  }, [smMediaDown]);

  const closeSubscriptionPopover = () => {
    setAnchorEl(null);
  };

  const addOrEdit = () => {
    batch(() => {
      dispatch(setPopup(false));
      dispatch(setItemForEdit(null));
    });
  };

  const openInPopup = (item) => {
    dispatch(setFormSimpleTouched(false));

    if (isNotificationTable) {
      batch(() => {
        dispatch(setNotification(item));

        dispatch(setPopupBasic(true));
      });

      return;
    }

    if (resource === 'bank-accounts') {
      batch(() => {
        dispatch(setItemForEditBasic(item));

        dispatch(setPopup(true));
      });

      return;
    }

    if (resource === 'countries' || resource === 'currencies') {
      return;
    }

    batch(() => {
      dispatch(setItemForEdit(item));
      dispatch(setPopup(true));
    });
  };

  const handleAdd = () => {
    batch(() => {
      dispatch(setItemForEdit(null));
      dispatch(setItemForEditBasic(null));
      dispatch(setPopup(true));
    });
  };

  const handleRefresh = async () => {
    if (typeof onRefresh !== 'function' || isFetching) {
      return;
    }

    try {
      setSearch('');
      setPage(0);

      await onRefresh();
    } catch (error) {
      console.error(`${resource} refresh failed:`, error);

      toast.error(() => t('toast.not_updated_try_again'));
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog((current) => ({
      ...current,
      isOpen: false,
    }));

    if (!onDelete) {
      return;
    }

    try {
      await onDelete(id);

      dispatch(setPopup(false));

      if (onAfterDelete) {
        await onAfterDelete(id);
      }

      toast.success(() => t('toast.record_successfully_deleted'), {
        style: {
          marginTop: resource === 'users' ? 60 : undefined,
        },
      });
    } catch (error) {
      console.error(`${resource} deletion failed:`, error);

      const status = getErrorStatus(error);

      if (status === 401 || status === 403) {
        toast.error(() => t('toast.only_account_manager_allowed'));

        return;
      }

      if (status === 404) {
        toast.error(() => t('toast.record_not_found'));

        return;
      }

      toast.error(() => t('toast.record_not_allowed_be_deleted'));
    }
  };

  const handleReadClick = async (item) => {
    if (!onMarkRead) {
      return;
    }

    try {
      await onMarkRead(item);
    } catch (error) {
      console.error('Notification update failed:', error);
    }
  };

  const handleSortRequest = (cellId) => {
    const isAscending = orderBy === cellId && order === 'asc';

    setOrder(isAscending ? 'desc' : 'asc');

    setOrderBy(cellId);
  };

  const subscriptionMessage = (() => {
    switch (resource) {
      case 'users':
        return t('toast.need_to_upgrade_in_order_to_register_new_users');

      case 'clients':
        return t('toast.need_to_upgrade_in_order_to_register_new_clients');

      case 'items-for-item-inv':
        return t('toast.need_to_upgrade_in_order_to_register_new_items_for_item_inv');

      default:
        return '';
    }
  })();

  return (
    <>
      {Boolean(addButtonText) && isFetching && (
        <Grid container item sx={{ mt: 1 }}>
          <LinearProgressWrapper />
        </Grid>
      )}

      <Grid container item sx={{ mt: 2 }} component={addButtonText ? Paper : undefined}>
        <Grid item sm={6} xs={12}>
          {canShowAddButton && (
            <>
              <ButtonAction
                color="primary"
                variant="outlined"
                size="medium"
                fullWidth={smMediaDown}
                disabled={isDeleting}
                onClick={disabledAddButton ? (event) => setAnchorEl(event.currentTarget) : handleAdd}
              >
                {addButtonText}
              </ButtonAction>

              <Popover
                open={openSubscriptionPopover}
                anchorEl={anchorEl}
                onClose={closeSubscriptionPopover}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
              >
                <TypographyPopover>{subscriptionMessage}</TypographyPopover>
              </Popover>
            </>
          )}
        </Grid>

        <Grid
          item
          sm={6}
          xs={12}
          sx={{
            mt: smMediaDown ? 1.5 : 0,
          }}
        >
          <QuickSearchInput
            quickSearchInput={search}
            handleQuickSearch={(event) => {
              setSearch(event?.target?.value?.toLowerCase() ?? '');
            }}
            clearQuickSearch={() => {
              setSearch('');
              setPage(0);
            }}
            handleRefresh={handleRefresh}
            refresh={isFetching}
          />
        </Grid>

        <TableContainer sx={{ mt: 1 }}>
          <TableStyled
            size="small"
            sx={{
              minWidth: smMediaDown ? undefined : 650,
            }}
          >
            <TableHead>
              <TableRowStyled>
                {columns.map((column) => (
                  <TableCellStyled
                    key={column.id}
                    align={column.align}
                    sx={{
                      minWidth: column.minWidth,

                      maxWidth: smMediaDown ? column.maxWidth : undefined,
                    }}
                  >
                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" fontWeight={550}>
                      {column.disableSorting ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={() => handleSortRequest(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      )}
                    </Typography>
                  </TableCellStyled>
                ))}

                {showActionsColumn && (
                  <TableCellStyled
                    align="left"
                    sx={{
                      minWidth: 110,
                      maxWidth: 130,
                      pl: 3,
                    }}
                  >
                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" fontWeight={550}>
                      {t('column.actions')}
                    </Typography>
                  </TableCellStyled>
                )}
              </TableRowStyled>
            </TableHead>

            <TableBody>
              {paginatedItems.map((row, index) => (
                <TableRowStyled
                  key={row.id ?? row.uf ?? index}
                  id={row.id}
                  onDoubleClick={() => openInPopup(row)}
                  onClick={() => setRowSelected(index)}
                  sx={{
                    backgroundColor: rowSelected === index ? '#fffbf2' : undefined,
                  }}
                >
                  {columns.map((column) => (
                    <TableCellStyled
                      key={column.id}
                      align={column.align}
                      sx={{
                        minWidth: column.minWidth,

                        maxWidth: column.maxWidth,
                      }}
                    >
                      <Typography noWrap textOverflow="ellipsis" overflow="hidden" fontWeight={450}>
                        {renderCellValue({
                          row,
                          column,
                          t,
                        })}
                      </Typography>
                    </TableCellStyled>
                  ))}

                  {showActionsColumn && !isNotificationTable && (
                    <TableCellStyled
                      align="left"
                      sx={{
                        minWidth: 90,
                        maxWidth: 130,
                      }}
                    >
                      <Stack direction="row">
                        <ButtonAction
                          color="primary"
                          style={{
                            maxWidth: '40px',
                            maxHeight: '20px',
                            minWidth: '40px',
                            minHeight: '20px',
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            openInPopup(row);
                          }}
                        >
                          <ModeEditOutlineIcon fontSize="small" />
                        </ButtonAction>

                        <ButtonAction
                          color="secondary"
                          disabled={isDeleting}
                          style={{
                            maxWidth: '40px',
                            maxHeight: '20px',
                            minWidth: '40px',
                            minHeight: '20px',
                          }}
                          onClick={(event) => {
                            event.stopPropagation();

                            setConfirmDialog({
                              isOpen: true,

                              title: t('popup.are_you_sure_to_delete_record'),

                              subTitle: (
                                <>
                                  <div>{t('popup.you_can_not_undo_this_operation')}</div>

                                  {resource === 'users' &&
                                    account?.is_manager &&
                                    String(account.id) === String(row.id) && (
                                      <div>{t('popup.additional_users_and_related_entries_will_be_deleted')}</div>
                                    )}
                                </>
                              ),

                              onConfirm: () => handleDelete(row.id),
                            });
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" color="secondary" />
                        </ButtonAction>
                      </Stack>
                    </TableCellStyled>
                  )}

                  {showActionsColumn && isNotificationTable && (
                    <TableCellStyled
                      align="left"
                      size="small"
                      sx={{
                        minWidth: 110,
                        maxWidth: 130,
                      }}
                    >
                      <Box
                        onClick={(event) => {
                          event.stopPropagation();

                          handleReadClick(row);
                        }}
                        sx={{
                          ml: 1.5,
                        }}
                      >
                        {row.read ? <MarkAsUnreadIcon color="warning" /> : <MarkunreadIcon color="primary" />}
                      </Box>
                    </TableCellStyled>
                  )}
                </TableRowStyled>
              ))}

              {Array.from({
                length: emptyRows,
              }).map((_, index) => (
                <TableRowStyled
                  key={`empty-${index}`}
                  sx={{
                    height: 37.5,
                  }}
                >
                  <TableCell colSpan={columns.length + (showActionsColumn ? 1 : 0)} />
                </TableRowStyled>
              ))}
            </TableBody>
          </TableStyled>
        </TableContainer>

        <TablePagination
          component="div"
          rowsPerPageOptions={pages}
          count={filteredItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => {
            setPage(newPage);
          }}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number.parseInt(event.target.value, 10));

            setPage(0);
          }}
        />
      </Grid>

      <Popup title={popupTitle}>
        {resource === 'clients' && <Contact addOrEdit={addOrEdit} itemForEdit={itemForEdit} />}

        {resource === 'bank-accounts' && <BankAccount addOrEdit={addOrEdit} />}

        {resource === 'items-for-item-inv' && <ItemInv addOrEdit={addOrEdit} />}

        {resource === 'terms-of-payment' && <TermOfPayment addOrEdit={addOrEdit} itemForEdit={itemForEdit} />}

        {resource === 'terms' && <ContractTerm addOrEdit={addOrEdit} itemForEdit={itemForEdit} />}

        {resource === 'users' && itemForEdit && (
          <AccountProfileDetails mode="user-management" account={account} itemForEdit={itemForEdit} />
        )}

        {resource === 'users' && !itemForEdit && <SignUpAddUser />}
      </Popup>

      <PopupBasic notification={notification} />

      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </>
  );
};

export default TableSimple;
